<template>
  <Transition
    enter-active-class="transform transition duration-500 ease-in-out"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-active-class="transform transition duration-500 ease-in-out"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <!--
      mt-16 keeps the panel below the global Navbar (h-16 = 4rem). If the
      navbar height ever changes, update here too or factor both values
      into a shared Tailwind design token.
    -->
    <div
      v-if="slideover.isOpen.value"
      ref="dialogRef"
      role="complementary"
      aria-labelledby="my-tasks-slideover-heading"
      :style="{ width: `${slideover.panelWidth.value}px` }"
      class="fixed inset-y-0 right-0 z-40 mt-16 flex flex-col border-l border-gray-200 bg-white shadow-xl"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between border-b border-gray-200 px-5 py-4"
      >
        <div class="flex items-center gap-2">
          <Icon name="check-circle" class="size-5 text-purple-500" />
          <h2
            id="my-tasks-slideover-heading"
            class="text-base font-semibold text-gray-800"
          >
            My Tasks
          </h2>
          <span
            v-if="tasks.data?.length"
            class="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-600"
          >
            {{ tasks.data.length }}
          </span>
        </div>
        <div class="flex items-center gap-1">
          <button
            v-tooltip="'Refresh'"
            type="button"
            aria-label="Refresh My Tasks list"
            class="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            @click="tasks.refetch()"
          >
            <Icon name="sync-alt" class="size-3.5" />
          </button>
          <button
            type="button"
            aria-label="Close My Tasks"
            class="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            @click="slideover.close()"
          >
            <Icon name="times" class="size-4" />
          </button>
        </div>
      </div>

      <!-- Task list -->
      <div class="flex-1 overflow-y-auto">
        <!-- Loading -->
        <div
          v-if="!tasks.isFetched && !tasks.isError"
          class="flex items-center justify-center py-12"
        >
          <Icon
            name="circle-solid"
            class="size-3 animate-pulse text-purple-400"
          />
        </div>

        <!-- Error state — only when there's no cached data to show. In
             TanStack Query v5, isError stays true while data remains
             from the previous successful fetch, so blanking the panel
             on every transient refetch failure was wiping the visible
             list for no good reason. -->
        <div
          v-else-if="tasks.isError && !tasks.data?.length"
          class="px-5 py-12 text-center"
        >
          <Icon
            name="exclamation-circle"
            class="mx-auto mb-3 size-10 text-amber-300"
          />
          <p class="text-sm font-medium text-gray-700">
            Couldn't load your tasks
          </p>
          <p class="mt-1 text-xs text-gray-500">
            Refresh the page to try again.
          </p>
        </div>

        <!-- Empty state -->
        <div v-else-if="!tasks.data?.length" class="px-5 py-12 text-center">
          <Icon
            name="check-circle"
            class="mx-auto mb-3 size-10 text-gray-200"
          />
          <p class="text-sm font-medium text-gray-500">No tasks assigned</p>
          <p class="mt-1 text-xs text-gray-400">
            Tasks assigned to you will appear here.
          </p>
        </div>

        <!-- Items -->
        <ul v-else class="divide-y divide-gray-100">
          <li v-for="item in tasks.data" :key="itemKey(item)" class="px-5 py-3">
            <div class="flex items-start gap-3">
              <!-- Status circle: interactive for AI tasks, read-only for recommendations -->
              <MyTaskStatusMenu
                v-if="item.type === 'ai_task'"
                :options="statusMenuItems(item)"
                :circle-class="statusCircleClass(item)"
              />
              <span
                v-else
                v-tooltip="itemStatusLabel(item)"
                class="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center"
              >
                <span
                  class="size-3.5 rounded-full border-2"
                  :class="statusCircleClass(item)"
                />
              </span>

              <!-- Content -->
              <div class="min-w-0 flex-1">
                <div class="flex items-start justify-between gap-2">
                  <RouterLink
                    :to="itemRoute(item)"
                    class="text-sm font-medium text-gray-800 hover:text-purple-600"
                    @click="slideover.close()"
                  >
                    {{ itemTitle(item) }}
                  </RouterLink>

                  <!-- Status badge -->
                  <span
                    class="mt-0.5 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    :class="statusBadgeClass(item)"
                  >
                    {{ itemStatusLabel(item) }}
                  </span>
                </div>

                <!-- Impact summary -->
                <p
                  v-if="item.impactSummary"
                  class="mt-0.5 truncate text-xs font-medium text-purple-600"
                >
                  &rarr; {{ item.impactSummary }}
                </p>

                <!-- Meta line -->
                <div
                  class="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-400"
                >
                  <span>{{ itemTag(item) }}</span>
                  <span class="text-gray-200">&middot;</span>
                  <span>{{ timeAgo(item.createdAt) }}</span>
                  <template
                    v-if="item.type === 'recommendation' && item.myCount"
                  >
                    <span class="text-gray-200">&middot;</span>
                    <span>
                      {{ item.myCount }} of {{ item.totalCount }} assigned to
                      you
                    </span>
                  </template>
                </div>

                <!-- Progress bar -->
                <div
                  v-if="hasProgress(item)"
                  class="mt-2 flex items-center gap-2"
                >
                  <div
                    class="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100"
                  >
                    <div
                      class="h-full rounded-full bg-purple-400 transition-all duration-300"
                      :style="{ width: `${progressPercent(item)}%` }"
                    />
                  </div>
                  <span class="text-[10px] font-medium text-gray-400">
                    {{ progressLabel(item) }}
                  </span>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useEventListener } from "@vueuse/core";
import Icon from "@/components/Core/Icon.vue";
import MyTaskStatusMenu from "@/components/Core/Recommendations/MyTaskStatusMenu.vue";
import { useFeatureFlagStatus } from "@/services/feature_flags";
import { useMyTasksSlideover } from "@/composables/useMyTasksSlideover";
import {
  useMyTasksQuery,
  useUpdateMyTaskStatusMutation,
  MyTaskItem,
} from "@/services/recommendations/service";
import { RecommendationTaskStatus } from "@/services/recommendations/tasks";
import {
  RecommendationType,
  RecommendationTypeDetails,
} from "@/services/recommendations/recommendations";
import { getCategoryMeta } from "@/services/recommendations/ai_suggestions";
import { toURLParam } from "@/utils/recommendations";
import { DateTime } from "luxon";

const slideover = useMyTasksSlideover();
// Only fetch My Tasks while the slideover is open. The panel mounts in
// the global shell, so without this gate the request fires on every
// page load even when the user never opens the slideover.
const tasks = useMyTasksQuery({ enabled: slideover.isOpen });
const statusMutation = useUpdateMyTaskStatusMutation();

// If the AI recommendations flag is revoked while the panel is open (admin
// toggle / kill-switch mid-session), close it so it stops showing AI tasks and
// the query (gated on isOpen) stops firing. Guard on isVisible (flag enabled),
// NOT isUsable: isUsable is also false when consent is merely pending, which
// would force-close the panel on a non-revoke transition.
const {
  isVisible: aiRecommendationsVisible,
  isLoading: aiFlagLoading,
  isError: aiFlagError,
} = useFeatureFlagStatus("aiRecommendations");
watch(
  [aiRecommendationsVisible, aiFlagLoading, aiFlagError],
  ([visible, loading, error]) => {
    // Only close on a genuine revoke. isVisible is also false while loading and
    // on a flag-fetch error, so guard on both — otherwise a transient
    // /feature-flags/ failure would close the panel like an admin revoke.
    if (!loading && !error && !visible && slideover.isOpen.value) {
      slideover.close();
    }
  },
  { immediate: true },
);

// The slideover is a non-modal landmark (role=complementary) — the rest
// of the page must remain interactive (clickable + focusable), e.g. so
// you can adjust a dashboard while watching task progress here. We
// therefore do NOT trap focus or set aria-modal=true; instead a global
// Escape listener closes the panel regardless of where focus currently
// lives, since opening the panel doesn't move focus into it.
const dialogRef = ref<HTMLElement | null>(null);

useEventListener(document, "keydown", (event) => {
  if (event.key === "Escape" && slideover.isOpen.value) {
    slideover.close();
  }
});

function itemKey(item: MyTaskItem): string {
  return item.type === "ai_task"
    ? `ai-${item.taskPk}`
    : `rec-${item.recommendation}`;
}

function itemTitle(item: MyTaskItem): string {
  if (item.type === "ai_task" && item.title) {
    return item.title;
  }
  const details =
    RecommendationTypeDetails[item.recommendation as RecommendationType];
  return details?.title ?? item.recommendation;
}

function itemTag(item: MyTaskItem): string {
  if (item.type === "ai_task" && item.category) {
    return getCategoryMeta(item.category).label;
  }
  const details =
    RecommendationTypeDetails[item.recommendation as RecommendationType];
  return details?.tag ?? "";
}

function itemRoute(item: MyTaskItem) {
  if (item.type === "ai_task" && item.taskPk) {
    return {
      name: "ai_recommendation_detail",
      params: { taskId: String(item.taskPk) },
    };
  }
  return {
    name: "recommendation-details",
    params: { type: toURLParam(item.recommendation as RecommendationType) },
  };
}

function itemStatusLabel(item: MyTaskItem): string {
  if (item.type === "ai_task") {
    return item.status ?? RecommendationTaskStatus.TO_DO;
  }
  // For recommendations: show dominant status. Mixed-closed groups
  // (some Done + some Dismissed = total, none open) shouldn't fall
  // through to "To do" — the group is fully closed, so prefer DONE
  // when any sub-task was completed, otherwise DISMISSED.
  const done = item.doneCount ?? 0;
  const dismissed = item.dismissedCount ?? 0;
  const total = item.totalCount ?? 0;
  if (total > 0 && done + dismissed === total) {
    return done > 0
      ? RecommendationTaskStatus.DONE
      : RecommendationTaskStatus.DISMISSED;
  }
  if ((item.inProgressCount ?? 0) > 0) {
    return RecommendationTaskStatus.IN_PROGRESS;
  }
  return RecommendationTaskStatus.TO_DO;
}

function timeAgo(isoDate: string): string {
  const dt = DateTime.fromISO(isoDate);
  const diff = dt.diffNow().negate();
  const days = Math.floor(diff.as("days"));
  if (days < 1) return "today";
  if (days === 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  const weeks = Math.floor(days / 7);
  if (weeks === 1) return "1 week ago";
  if (weeks < 4) return `${weeks} weeks ago`;
  const months = Math.floor(days / 30);
  if (months === 1) return "1 month ago";
  return `${months} months ago`;
}

// --- Progress ---

function hasProgress(item: MyTaskItem): boolean {
  if (item.type === "recommendation") {
    return (item.totalCount ?? 0) > 1;
  }
  return (item.totalSteps ?? 0) > 0;
}

function progressPercent(item: MyTaskItem): number {
  if (item.type === "recommendation") {
    const total = item.totalCount ?? 0;
    if (total === 0) return 0;
    // `itemStatusLabel` treats done + dismissed as a closed state, so
    // mirror that here — otherwise a 2-done / 2-dismissed group would
    // show "Done" with progress 50 %.
    const closed = (item.doneCount ?? 0) + (item.dismissedCount ?? 0);
    return Math.round((closed / total) * 100);
  }
  const total = item.totalSteps ?? 0;
  if (total === 0) return 0;
  return Math.round(((item.doneSteps ?? 0) / total) * 100);
}

function progressLabel(item: MyTaskItem): string {
  if (item.type === "recommendation") {
    const closed = (item.doneCount ?? 0) + (item.dismissedCount ?? 0);
    return `${closed}/${item.totalCount ?? 0}`;
  }
  return `${item.doneSteps ?? 0}/${item.totalSteps ?? 0}`;
}

// --- Status ---

// Per-row pending guard so a status PATCH on one row doesn't drop
// quick triage clicks on every other row in the panel. The
// component-wide `statusMutation.isPending` was too broad: while one
// in-flight PATCH was pending, subsequent menu picks elsewhere in the
// panel silently no-op'd and left the user wondering whether the
// click registered. Track each in-flight `itemKey(item)` in a Set so
// concurrent PATCHes on different rows are each protected
// independently — a single-ref version only remembered the latest row
// and let earlier mid-flight rows accept duplicate writes.
const pendingRowKeys = ref<Set<string>>(new Set());

function updateStatus(
  item: MyTaskItem,
  status: RecommendationTaskStatus,
): void {
  const rowKey = itemKey(item);
  // Skip if this exact row is already mid-PATCH (the only race that
  // matters: response order would otherwise pick the final state).
  // No-op writes also bail early.
  if (pendingRowKeys.value.has(rowKey)) return;
  if (status === itemStatusLabel(item)) return;
  pendingRowKeys.value.add(rowKey);
  statusMutation.mutate(
    {
      recommendation: item.recommendation,
      status,
      taskPk: item.taskPk,
    },
    {
      onSettled: () => {
        pendingRowKeys.value.delete(rowKey);
      },
    },
  );
}

function statusMenuItems(
  item: MyTaskItem,
): { title: string; action: () => void; selected?: boolean }[] {
  // Use itemStatusLabel for both branches so the menu state stays in
  // sync with the badge / circle (which already do). Reading raw
  // item.status for AI tasks lets a null value render the menu with
  // nothing selected even though the rest of the row already
  // normalized that to "To do".
  const currentStatus = itemStatusLabel(item);
  const statuses = [
    RecommendationTaskStatus.TO_DO,
    RecommendationTaskStatus.IN_PROGRESS,
    RecommendationTaskStatus.DONE,
    RecommendationTaskStatus.DISMISSED,
  ];
  return statuses.map((s) => ({
    title: s,
    action: () => updateStatus(item, s),
    selected: currentStatus === s,
  }));
}

function statusCircleClass(item: MyTaskItem): string {
  const status = item.type === "ai_task" ? item.status : itemStatusLabel(item);
  switch (status) {
    case RecommendationTaskStatus.IN_PROGRESS:
      return "border-blue-400 bg-blue-400";
    case RecommendationTaskStatus.DONE:
      return "border-green-500 bg-green-500";
    case RecommendationTaskStatus.DISMISSED:
      return "border-gray-300 bg-gray-300";
    default:
      return "border-gray-300 bg-transparent";
  }
}

function statusBadgeClass(item: MyTaskItem): string {
  const status = itemStatusLabel(item);
  switch (status) {
    case RecommendationTaskStatus.IN_PROGRESS:
      return "bg-blue-50 text-blue-600";
    case RecommendationTaskStatus.DONE:
      return "bg-green-50 text-green-600";
    case RecommendationTaskStatus.DISMISSED:
      return "bg-gray-200 text-gray-600";
    case RecommendationTaskStatus.TO_DO:
      return "bg-gray-100 text-gray-600";
    default:
      return "bg-gray-100 text-gray-500";
  }
}
</script>
